import React, { useState, forwardRef, useEffect, useRef } from 'react'
import {
  Button,
  Collapse,
  Input,
  Radio,
  Space,
  Typography,
  Upload,
  message,
} from 'antd'
import {
  PlusOutlined,
  MinusCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import TextArea from 'antd/es/input/TextArea'
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  FieldValues,
} from 'react-hook-form'
import {
  parseExcelFile,
  validateExcelFile,
  downloadTemplate,
} from '../../../../utils/excelImport'
import DefaultEditor from 'react-simple-wysiwyg'

const { Panel } = Collapse
const { Title } = Typography

const MAX_ANSWERS = 6
export interface AddQuestionFormProps {
  name: string
  register: UseFormRegister<FieldValues>
  errors: FieldErrors
  setValue: UseFormSetValue<FieldValues>
  watch: UseFormWatch<FieldValues>
}

const AddQuestionForm = forwardRef<any, AddQuestionFormProps>(
  ({ name, register, errors, setValue, watch }, ref) => {
    const [questionTitles, setQuestionTitles] = useState<string[]>(
      watch(name).map((q: any) => q.question || ''),
    )
    const [isImporting, setIsImporting] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      const questions = watch(name)
      setQuestionTitles(questions.map((q: any) => q.question || ''))
    }, [watch, name])

    const addQuestion = () => {
      const currentQuestions = watch(name)
      const newQuestions = [
        ...(currentQuestions || []),
        {
          question: '',
          discussion: '',
          normal_lab: '',
          image: null,
          answers: [{ choice: '', is_true: false }],
        },
      ]
      setValue(name, newQuestions, { shouldValidate: true })
      setQuestionTitles((prev) => [...prev, ''])
    }

    const removeQuestion = (index: number) => {
      const currentQuestions = watch(name)
      const newQuestions = currentQuestions.filter(
        (_: any, i: number) => i !== index,
      )
      setValue(name, newQuestions, { shouldValidate: true })
      setQuestionTitles((prev) => prev.filter((_, i) => i !== index))
    }

    const addAnswer = (questionIndex: number) => {
      const currentQuestions = watch(name)
      const currentAnswers = currentQuestions[questionIndex].answers || []
      const newAnswers = [...currentAnswers, { choice: '', is_true: false }]
      setValue(`${name}.${questionIndex}.answers`, newAnswers, {
        shouldValidate: true,
      })
    }

    const removeAnswer = (questionIndex: number, answerIndex: number) => {
      const currentQuestions = watch(name)
      const currentAnswers = currentQuestions[questionIndex].answers
      const newAnswers = currentAnswers.filter(
        (_: any, i: number) => i !== answerIndex,
      )
      setValue(`${name}.${questionIndex}.answers`, newAnswers, {
        shouldValidate: true,
      })
    }

    const handleRadioChange = (
      questionIndex: number,
      selectedAnswerIndex: number,
    ) => {
      const currentQuestions = watch(name)
      const currentAnswers = currentQuestions[questionIndex].answers
      const updatedAnswers = currentAnswers.map((answer: any, idx: number) => ({
        ...answer,
        is_true: idx === selectedAnswerIndex,
      }))
      setValue(`${name}.${questionIndex}.answers`, updatedAnswers, {
        shouldValidate: true,
      })
    }

    const handleFileUpload = async (file: File) => {
      setIsImporting(true)
      try {
        // Validate file
        const validationError = validateExcelFile(file)
        if (validationError) {
          message.error(validationError)
          return false
        }

        // Parse Excel file
        const parsedQuestions = await parseExcelFile(file)

        // Update form with parsed questions
        const currentQuestions = watch(name) || []
        const newQuestions = [...currentQuestions, ...parsedQuestions.map(q => ({
          ...q,
          image: q.image || null,
        }))]


        setValue(name, newQuestions, { shouldValidate: true })
        setQuestionTitles(newQuestions.map((q: any) => q.question || ''))

        message.success(
          `Successfully imported ${parsedQuestions.length} questions from Excel file`,
        )
      } catch (error) {
        console.error('Import error:', error)
        message.error(`Import failed: ${(error as Error).message}`)
      } finally {
        setIsImporting(false)
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }

      return false // Prevent default upload behavior
    }

    const handleDownloadTemplate = () => {
      try {
        downloadTemplate()
        message.success('Template downloaded successfully')
      } catch (error) {
        message.error('Failed to download template')
      }
    }

    const replaceAllQuestions = async (file: File) => {
      setIsImporting(true)
      try {
        // Validate file
        const validationError = validateExcelFile(file)
        if (validationError) {
          message.error(validationError)
          return false
        }

        // Parse Excel file
        const parsedQuestions = await parseExcelFile(file)

        // Replace all questions with parsed ones
        setValue(name, parsedQuestions, { shouldValidate: true })
        setQuestionTitles(parsedQuestions.map((q: any) => q.question || ''))

        message.success(
          `Successfully replaced with ${parsedQuestions.length} questions from Excel file`,
        )
      } catch (error) {
        console.error('Import error:', error)
        message.error(`Import failed: ${(error as Error).message}`)
      } finally {
        setIsImporting(false)
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }

      return false // Prevent default upload behavior
    }

    // Helper to get nested error
    const getNestedError = (
      errorObject: FieldErrors,
      pathSegments: (string | number)[],
    ) => {
      let currentError: any = errorObject
      for (const segment of pathSegments) {
        if (currentError && currentError[segment]) {
          currentError = currentError[segment]
        } else {
          return undefined
        }
      }
      return currentError?.message
    }

    return (
      <div>
        <Title level={4}>Question & Answer</Title>

        <Collapse accordion>
          {watch(name).map((question: any, questionIndex: number) => {
            const questionBaseName = `${name}.${questionIndex}`
            const questionError = getNestedError(errors, [
              name,
              questionIndex,
              'question',
            ])
            const discussionError = getNestedError(errors, [
              name,
              questionIndex,
              'discussion',
            ])
            const normalLabError = getNestedError(errors, [
              name,
              questionIndex,
              'normal_lab',
            ])
            const answersError = getNestedError(errors, [
              name,
              questionIndex,
              'answers',
            ]) // For answers array error (min 1, one correct)

            const cleanTitle = (questionTitles[questionIndex] || '')
              .replace(/<[^>]*>?/gm, '')
              .trim()

            return (
              <Panel
                header={
                  <strong>
                    {cleanTitle || `Pertanyaan #${questionIndex + 1}`}
                  </strong>
                }
                key={questionIndex} // Use index as key (should be stable for this use case)
                extra={
                  <MinusCircleOutlined
                    onClick={(e) => {
                      e.stopPropagation()
                      removeQuestion(questionIndex)
                    }}
                  />
                }
              >
                {/* Image */}
                <div className="input-block mt-3">
                  <label className="form-label">Gambar</label>
                  <div className="row align-items-center">
                    <div className="col-md-10">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="No file selected"
                        readOnly
                        value={
                          watch(`${questionBaseName}.image`) ||
                          ''
                        }
                      />
                    </div>
                    <div className="col-md-2 d-grid">
                      <label
                        htmlFor={`question-${questionIndex}-image-upload`}
                        className="btn btn-secondary text-center"
                      >
                        Upload File
                      </label>
                      <input
                        type="file"
                        id={`question-${questionIndex}-image-upload`}
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          // Update form value
                          setValue(`${questionBaseName}.image`, file, { shouldValidate: true })
                        }}
                      />
                    </div>
                  </div>

                  {/* Preview Image */}
                  {watch(`${questionBaseName}.image`) && (
                    <div className="mt-2">
                      <img
                        src={
                          watch(`${questionBaseName}.image`)
                            ? watch(`${questionBaseName}.image`) instanceof File
                              ? URL.createObjectURL(watch(`${questionBaseName}.image`))
                                                : `${process.env.REACT_APP_BASE_URL_API}/storage/${watch(`${questionBaseName}.image`)}`
                            : ''
                        }
                        alt="Preview"
                        style={{ maxWidth: '200px', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                </div>

                {/* Pertanyaan */}
                <div className="input-block">
                  <label className="form-label">
                    Pertanyaan <span className="text-danger ms-1">*</span>
                  </label>
                  <div className="summernote">
                    <DefaultEditor
                      value={watch(`${questionBaseName}.question`) || ''}
                      onChange={(e) => {
                        setValue(
                          `${questionBaseName}.question`,
                          e.target.value,
                          { shouldValidate: true },
                        )
                        const newTitles = [...questionTitles]
                        newTitles[questionIndex] = e.target.value
                        setQuestionTitles(newTitles)
                      }
                      }
                      className={questionError ? 'is-invalid' : ''}
                    />
                  </div>
                  {questionError && (
                    <div className="text-danger">{questionError}</div>
                  )}
                </div>

                {/* Jawaban */}
                <div className="input-block mt-3">
                  <label className="form-label">
                    Jawaban <span className="text-danger ms-1">*</span>
                  </label>
                  {question.answers &&
                    question.answers.map((answer: any, answerIndex: number) => {
                      const answerBaseName = `${questionBaseName}.answers.${answerIndex}`
                      const choiceError = getNestedError(errors, [
                        name,
                        questionIndex,
                        'answers',
                        answerIndex,
                        'choice',
                      ])
                      const selectedTrueIndex = question.answers.findIndex(
                        (a: any) => a?.is_true,
                      )

                      return (
                        <Space
                          key={answerIndex} // Use index as key
                          align="start"
                          style={{ display: 'flex', marginBottom: 8 }}
                        >
                          <div className="flex-grow-1">
                            {' '}
                            {/* Wrapper for input and error */}
                            <Input
                              placeholder={`Jawaban ${answerIndex + 1}`}
                              {...register(`${answerBaseName}.choice`)}
                              value={watch(`${answerBaseName}.choice`) || ''} // Pastikan value terkontrol
                              onChange={(e) =>
                                setValue(
                                  `${answerBaseName}.choice`,
                                  e.target.value,
                                  { shouldValidate: true },
                                )
                              }
                              className={choiceError ? 'is-invalid' : ''}
                            />
                            {choiceError && (
                              <div className="text-danger">{choiceError}</div>
                            )}
                          </div>

                          <Radio
                            checked={selectedTrueIndex === answerIndex}
                            onChange={() =>
                              handleRadioChange(questionIndex, answerIndex)
                            }
                          />

                          {question.answers.length > 1 && (
                            <MinusCircleOutlined
                              onClick={() =>
                                removeAnswer(questionIndex, answerIndex)
                              }
                            />
                          )}
                        </Space>
                      )
                    })}

                  {/* Error untuk array jawaban (minimal satu, satu yang benar) */}
                  {answersError && (
                    <div className="text-danger">{answersError}</div>
                  )}

                  {question.answers.length < MAX_ANSWERS && (
                    <Button
                      type="dashed"
                      onClick={() => addAnswer(questionIndex)}
                      icon={<PlusOutlined />}
                      block
                    >
                      Tambah Jawaban
                    </Button>
                  )}
                </div>

                {/* Normal Lab */}
                <div className="input-block mt-3">
                  <label className="form-label">Normal Lab</label>
                  <Input
                    placeholder="Masukkan data normal lab"
                    {...register(`${questionBaseName}.normal_lab`)}
                    value={watch(`${questionBaseName}.normal_lab`) || ''} // Pastikan value terkontrol
                    onChange={(e) =>
                      setValue(
                        `${questionBaseName}.normal_lab`,
                        e.target.value,
                        { shouldValidate: true },
                      )
                    }
                    className={normalLabError ? 'is-invalid' : ''}
                  />
                  {normalLabError && (
                    <div className="text-danger">{normalLabError}</div>
                  )}
                </div>

                {/* Pembahasan */}
                <div className="input-block mt-3">
                  <label className="form-label">Pembahasan</label>
                  <div className="summernote">
                    <DefaultEditor
                      value={watch(`${questionBaseName}.discussion`) || ''}
                      onChange={(e) =>
                        setValue(
                          `${questionBaseName}.discussion`,
                          e.target.value,
                          { shouldValidate: true },
                        )
                      }
                      className={discussionError ? 'is-invalid' : ''}
                    />
                  </div>
                  {discussionError && (
                    <div className="text-danger">{discussionError}</div>
                  )}
                </div>
              </Panel>
            )
          })}
        </Collapse>

        <div className="mt-5">
          {/* Excel Import Section */}
          <div className="mb-3">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                  >
                    Download Template Excel
                  </Button>

                  <Upload
                    accept=".xlsx,.xls"
                    showUploadList={false}
                    beforeUpload={handleFileUpload}
                    disabled={isImporting}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={isImporting}
                      type="default"
                    >
                      Import dari Excel (Tambah)
                    </Button>
                  </Upload>

                  <Upload
                    accept=".xlsx,.xls"
                    showUploadList={false}
                    beforeUpload={replaceAllQuestions}
                    disabled={isImporting}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={isImporting}
                      danger
                    >
                      Import dari Excel (Ganti Semua)
                    </Button>
                  </Upload>
                </Space>
              </div>

              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Format Excel:</strong> Pertanyaan | Jawaban A | Jawaban
                B | Jawaban C | Jawaban D | Jawaban E | Normal Lab | Pembahasan
                <br />
                <strong>Jawaban benar:</strong> Tandai dengan [x] di depan
                pilihan jawaban (contoh: [x] Jakarta)
              </div>
            </Space>
          </div>

          <Button
            type="dashed"
            onClick={addQuestion}
            icon={<PlusOutlined />}
            block
          >
            Tambah Pertanyaan Manual
          </Button>
        </div>
      </div>
    )
  },
)

export default AddQuestionForm
